import React, { useState } from 'react';
import { X, CheckCircle2, ShieldAlert, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';

const REQUEST_TYPES = [
  {
    id: 'delete_all',
    label: 'Удалить аккаунт и все мои данные',
    hint: 'Полное удаление учётной записи, истории чатов и медицинских записей.'
  },
  {
    id: 'revoke_medical_consent',
    label: 'Отозвать согласие на обработку медицинских данных',
    hint: 'Аккаунт сохранится, но медицинские документы и метрики здоровья будут удалены.'
  },
  {
    id: 'data_export',
    label: 'Получить копию всех моих данных',
    hint: 'Выписка в машиночитаемом формате (JSON) на указанный email.'
  },
  {
    id: 'correction',
    label: 'Исправить или уточнить мои данные',
    hint: 'Если данные в системе указаны неточно — опишите, что нужно поправить.'
  }
];

const API = process.env.REACT_APP_BACKEND_URL;

const DataDeletionRequestModal = ({ isOpen, onClose }) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [requestType, setRequestType] = useState('delete_all');
  const [description, setDescription] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  if (!isOpen) return null;

  const reset = () => {
    setFullName('');
    setEmail('');
    setRequestType('delete_all');
    setDescription('');
    setConsent(false);
    setError('');
    setSuccess(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!fullName.trim() || fullName.trim().length < 2) {
      setError('Укажите ФИО');
      return;
    }
    if (!email.includes('@') || !email.includes('.')) {
      setError('Введите корректный email');
      return;
    }
    if (!consent) {
      setError('Подтвердите, что запрос подаётся владельцем данных');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/legal/data-deletion-request`, {
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
        request_type: requestType,
        description: description.trim() || null,
        consent_confirmed: true
      });
      setSuccess(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось отправить запрос. Попробуйте позже.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 px-4 py-8"
      onClick={handleClose}
      data-testid="dsr-modal-backdrop"
    >
      <div
        className="liquid-glass-card relative w-full max-w-lg max-h-[90vh] overflow-hidden rounded-[28px]"
        onClick={(e) => e.stopPropagation()}
        data-testid="dsr-modal"
      >
        {/* Header */}
        <div className="relative z-10 flex items-start justify-between px-6 pt-6 pb-4 border-b border-white/8">
          <div className="pr-10">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-cyan-300/80 mb-1">
              <ShieldAlert className="w-3.5 h-3.5" />
              152-ФЗ · ст. 14, 21
            </div>
            <h2 className="text-lg font-medium text-white" data-testid="dsr-title">
              Запрос на удаление или изменение данных
            </h2>
            <p className="text-xs text-white/55 mt-1">
              Срок ответа Оператора — до 30 календарных дней
            </p>
          </div>
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="dsr-close-btn"
          >
            <X className="w-5 h-5 text-white/70" />
          </button>
        </div>

        {/* Body */}
        {success ? (
          <div className="relative z-10 px-6 py-8 space-y-5" data-testid="dsr-success">
            <div className="flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-400" />
              </div>
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-base font-medium text-white">Запрос принят</h3>
              <p className="text-sm text-white/65 leading-relaxed">
                Мы зафиксировали ваше обращение. Ответ направим на указанный email в срок до 30 календарных дней (ст. 21 ФЗ № 152-ФЗ).
              </p>
            </div>
            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-white/55">Номер запроса</span>
                <span className="text-white font-mono" data-testid="dsr-request-number">{success.request_number}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-white/55">Email для связи</span>
                <span className="text-white text-xs">{email}</span>
              </div>
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/8">
                <span className="text-white/55 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  Контакт DPO
                </span>
                <a href="mailto:privacy@aimedguru.ru" className="text-cyan-300 text-xs">privacy@aimedguru.ru</a>
              </div>
            </div>
            <Button
              onClick={handleClose}
              className="w-full glass-btn text-white rounded-full h-11"
              data-testid="dsr-success-close-btn"
            >
              Закрыть
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <ScrollArea className="max-h-[calc(90vh-180px)]">
              <div className="relative z-10 px-6 py-5 space-y-5">
                {/* ФИО */}
                <div className="space-y-1.5">
                  <label className="text-xs text-white/55 font-medium">ФИО *</label>
                  <Input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Иванов Иван Иванович"
                    className="auth-input-glass text-white placeholder:text-white/35 rounded-full h-11 px-5"
                    disabled={submitting}
                    data-testid="dsr-fullname"
                  />
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label className="text-xs text-white/55 font-medium">Email для связи *</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="auth-input-glass text-white placeholder:text-white/35 rounded-full h-11 px-5"
                    disabled={submitting}
                    data-testid="dsr-email"
                  />
                  <p className="text-[11px] text-white/40">Желательно использовать тот же email, что и в аккаунте — это ускорит идентификацию.</p>
                </div>

                {/* Тип запроса */}
                <div className="space-y-2">
                  <label className="text-xs text-white/55 font-medium">Тип запроса *</label>
                  <div className="space-y-2">
                    {REQUEST_TYPES.map((rt) => (
                      <button
                        key={rt.id}
                        type="button"
                        onClick={() => setRequestType(rt.id)}
                        disabled={submitting}
                        className={`w-full text-left p-3 rounded-2xl border transition-all ${
                          requestType === rt.id
                            ? 'bg-cyan-500/10 border-cyan-400/50'
                            : 'bg-white/[0.025] border-white/8 hover:border-white/16'
                        }`}
                        data-testid={`dsr-type-${rt.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 transition-all ${
                            requestType === rt.id ? 'border-cyan-400 bg-cyan-400' : 'border-white/30'
                          }`}>
                            {requestType === rt.id && (
                              <div className="w-1.5 h-1.5 rounded-full bg-black m-auto mt-[3px]" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="text-sm text-white font-medium">{rt.label}</div>
                            <div className="text-[11px] text-white/50 mt-0.5 leading-snug">{rt.hint}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Описание */}
                <div className="space-y-1.5">
                  <label className="text-xs text-white/55 font-medium">Дополнительные сведения (необязательно)</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Например: какие именно данные нужно исправить."
                    rows={3}
                    maxLength={500}
                    disabled={submitting}
                    className="auth-input-glass w-full text-white placeholder:text-white/35 rounded-2xl px-5 py-3 text-sm resize-none focus:outline-none"
                    data-testid="dsr-description"
                  />
                  <p className="text-[11px] text-white/40 text-right">{description.length} / 500</p>
                </div>

                {/* Consent */}
                <div className="flex items-start gap-3 p-3 rounded-2xl bg-white/[0.025] border border-white/8">
                  <Checkbox
                    id="dsr-consent"
                    checked={consent}
                    onCheckedChange={(c) => setConsent(!!c)}
                    className="mt-0.5 border-white/30 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                    data-testid="dsr-consent"
                  />
                  <label htmlFor="dsr-consent" className="text-xs text-white/65 leading-relaxed cursor-pointer">
                    Подтверждаю, что данный запрос подаётся мной как субъектом персональных данных. Я понимаю, что Оператор вправе запросить дополнительные сведения для подтверждения личности (п. 7 ст. 14 ФЗ № 152-ФЗ).
                  </label>
                </div>

                {error && (
                  <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-xl p-3" data-testid="dsr-error">
                    {error}
                  </div>
                )}
              </div>
            </ScrollArea>

            <div className="relative z-10 px-6 py-4 border-t border-white/8 flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="glass-btn text-white rounded-full h-11 flex-1 sm:flex-none"
                data-testid="dsr-cancel-btn"
              >
                Отмена
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="glass-btn-accent text-white rounded-full h-11 flex-1 font-medium"
                data-testid="dsr-submit-btn"
              >
                {submitting ? 'Отправка…' : 'Отправить запрос'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DataDeletionRequestModal;
